import type { FC } from "react"
import React, { useState } from "react"

import { atom, useRecoilState, useRecoilValue } from "recoil"
import styled from "styled-components"

import Button from "~/components/Button"
import { useDesignRequest } from "~/containers/Proposal/state/design-request/use-design-request"
import { designIdState } from "~/containers/Proposal/state/designs/design-state"
import { useProposalFetcher } from "~/containers/Proposal/state/proposal/proposal-fetchers"
import { proposalRemoteState } from "~/containers/Proposal/state/proposal/proposal-state"
import { Dropdown } from "~/ui/lib/options/Select/Dropdown"

import { DrawerContainer } from "."

export type RevisionType = `over-night` | `realtime`

export const DesignRevisionDrawerOpenState = atom<boolean>({
  key: `DesignRevisionDrawer`,
  default: false,
})

const REVISION_REASONS = [
  `Updated Design`,
  `Incorrect Location or Address`,
  `Change Installer`,
  `Change in Customer Contact Information`,
  `Layout - Panel Size`,
  `Shade Value Adjustment`,
]

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  width: 450px;
  overflow-y: auto;
  padding-bottom: 30px;
  p {
    font-size: 14px;
    color: var(--grey-dark);
  }
  h2 {
    font-size: 20px;
    font-weight: 500;
    text-transform: capitalize;
  }
`
const Top = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  h2 {
    display: flex;
    align-items: center;
    gap: 15px;
  }
`
export const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
  textarea {
    width: 100%;
    height: 100%;
    border: 1px solid #b1c2d9;
    border-radius: 6px;
    padding: 20px;
    font-size: 16px;
    ::placeholder {
      color: #b1c2d9;
    }
  }
  padding-bottom: 30px;
`
export const CheckBoxes = styled.div`
  padding-bottom: 30px;
  display: flex;
  flex-direction: column;
  gap: 15px;
  div {
    display: flex;
    gap: 10px;
  }
  input {
    cursor: pointer;
  }
  label {
    cursor: pointer;
    font-size: 16px;
    font-weight: 600;
    color: var(--blue-dark);
  }
`

const revisionTypeState = atom<string>({
  key: `revisionType`,
  default: `over-night`,
})

const designRevisionLoadingState = atom<boolean>({
  key: `designRevisionLoading`,
  default: false,
})

const DesignRevisionDrawer_Internal: FC = () => {
  const [designRevision, setDesignRevision] = useRecoilState(
    DesignRevisionDrawerOpenState,
  )
  const [revisionType, setRevisionType] = useRecoilState(revisionTypeState)
  const [inFlight, setInFlight] = useRecoilState(designRevisionLoadingState)
  const [reasons, setReasons] = useState<string[]>([])
  const [details, setDetails] = useState(``)
  const { requestRevision } = useDesignRequest()
  const parentId = useRecoilValue(designIdState)
  const remote = useRecoilValue(proposalRemoteState)
  const refetchProposal = useProposalFetcher({ slug: remote.slug })

  const detailsAndReasons = details.length > 0 && reasons.length > 0

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!designRevision) return
    if (!detailsAndReasons) return
    setInFlight(true)
    await requestRevision({
      reasons,
      notes: details,
      parentId,
      revisionType: revisionType,
    })
    await refetchProposal.refetch()

    setDesignRevision(false)
    setInFlight(false)
  }

  return (
    <DrawerContainer
      isOpen={designRevision}
      close={() => setDesignRevision(false)}
      title="Request New Design"
    >
      <Wrapper>
        <Top>
          <h2>
            Request New Design
            <span>
              <Dropdown.Container
                SelectedOption={() => <p>{revisionType}</p>}
                Options={[`over-night`, `realtime`].map((option) => (
                  <Dropdown.Item
                    label1={option}
                    key={option}
                    onClick={() => setRevisionType(option)}
                    isChecked={option === revisionType}
                  />
                ))}
              />
            </span>
          </h2>
          <p>
            Check the boxes below which summarize why your homeowner wants a new
            proposal.
          </p>
        </Top>
        <StyledForm onSubmit={handleSubmit}>
          <CheckBoxes>
            {REVISION_REASONS.map((reason, index) => (
              <div key={reason}>
                <input
                  type="checkbox"
                  id={`reason${index}`}
                  name={`reason${index}`}
                  value={reason}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setReasons((prev) => [...prev, e.target.value])
                    } else {
                      setReasons((prev) =>
                        prev.filter((r) => r !== e.target.value),
                      )
                    }
                  }}
                />
                <label htmlFor={`reason${index}`}>{reason}</label>
              </div>
            ))}
          </CheckBoxes>
          <p>
            Please provide any additional or relevant details that are critical
            to your redesign being done correctly. Be specific as you can:
          </p>
          <textarea
            name="details"
            id="details"
            cols={30}
            rows={10}
            placeholder="Please provide additional details relating to your revision request. (Maximum 1000 characters)"
            onChange={(e) => setDetails(e.target.value)}
          />
          <Footer>
            <Button type="button" onClick={() => setDesignRevision(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={inFlight || !detailsAndReasons}>
              Request New Design
            </Button>
          </Footer>
        </StyledForm>
      </Wrapper>
    </DrawerContainer>
  )
}
export const DesignRevisionDrawer = React.memo(DesignRevisionDrawer_Internal)

export const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: absolute;
  z-index: 99999;
  bottom: 0;
  left: 0;
  right: 0;
  height: 75px;
  padding: 16px 35px;
  gap: 30px;
  background-color: rgba(0, 112, 244, 0.1);
  button {
    height: 100%;
    width: 100%;
    position: relative;
  }
`
